import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, TextField, Typography } from '@mui/material';
import { CheckoutButton, CheckoutCardHeader } from './checkoutStyle';
import { createShoppingCartsClient } from '../../helpers/client';
import { loadCartAsync, resetCouponCode, assignCouponCode } from '../../redux/reducers/shoppingCartReducer';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { ShoppingCartCouponType } from 'orderme-api-integration-client';
import { roundDoubleNumbers } from '../../helpers/currency';
import { ButtonLoadingIndicator } from '../../controls';

export function DiscountCode() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const cartClient = useMemo(() => createShoppingCartsClient(), []);
  const coupon = useAppSelector((state: RootState) => state.shoppingCartState.coupon);
  const [inputCode, setInputCode] = useState<string>('');
  const [couponCode, setCouponCode] = useState<string>('');
  const [couponError, setCouponError] = useState<string>('');
  const [inProgress, setInProgress] = useState(false);

  useEffect(() => {
    if (coupon) {
      setCouponCode(coupon.code);
    } else {
      setCouponCode('');
    }
  }, [coupon]);

  const handleUseCouponClick = async () => {
    setInProgress(true);
    try {
      const code = inputCode.trim();
      const isValid = await validateCouponCode(code);
      if (isValid) {
        setCouponError('');
        setInputCode('');
      } else {
        setCouponError(t('payment:invalidDiscountCode'));
      }
    } finally {
      setInProgress(false);
    }
  };

  const handleCancelCouponClick = async () => {
    setInProgress(true);
    try {
      dispatch(resetCouponCode());
      await cartClient.removeUsedCoupon();
      dispatch(loadCartAsync());
      setCouponCode('');
      setInputCode('');
      setCouponError('');
    } catch (ex) {
      console.error('Not possible to remove used coupon', ex);
    } finally {
      setInProgress(false);
    }
  };

  const validateCouponCode = async (code: string): Promise<boolean> => {
    await new Promise((r) => setTimeout(r, 1000));
    if (!code.trim()) {
      return false;
    }
    try {
      var result = await cartClient.useCoupon(code);
      if (!result) {
        return false;
      }
      dispatch(assignCouponCode(code));
      dispatch(loadCartAsync());
      return true;
    } catch {
      return false;
    }
  };

  return (
    <Box>
      <CheckoutCardHeader>{t('payment:discountCode')}</CheckoutCardHeader>
      {!couponCode && (
        <>
          <Typography variant="body2" sx={{ mb: '10px' }}>
            {t('payment:enterDiscountCode')}
          </Typography>
          <Grid container direction="row" spacing={2} sx={{ justifyContent: 'space-between' }}>
            <Grid size="grow">
              <TextField
                fullWidth
                value={inputCode}
                disabled={inProgress}
                onChange={(e) => setInputCode(e.target.value)}
                slotProps={{
                  input: {
                    sx: {
                      borderRadius: '10px',
                      height: '30px',
                      fontSize: '16px',
                      '& input': {
                        textAlign: 'right',
                      },
                    },
                  },
                }}
              />
            </Grid>
            <Grid size="auto">
              <CheckoutButton
                variant="contained"
                disabled={inProgress || !inputCode.trim()}
                onClick={handleUseCouponClick}
                loading={inProgress}
                loadingIndicator={<ButtonLoadingIndicator size={18} />}
              >
                {t('order:apply')}
              </CheckoutButton>
            </Grid>
          </Grid>
          {couponError && (
            <Typography variant="body2" color="error.main">
              {couponError}
            </Typography>
          )}
        </>
      )}
      {couponCode && coupon && (
        <>
          <Grid container direction="row" spacing={2} sx={{ justifyContent: 'space-between' }}>
            <Grid size="grow">
              <Typography sx={{ fontStyle: 'italic' }} variant="body2">
                {couponCode}
              </Typography>
              {coupon.type === ShoppingCartCouponType.PercentageDiscount && (
                <Typography variant="body2">
                  {coupon.value === 0 ? (
                    <>
                      {t('payment:couponMinimumPrice')}
                      {roundDoubleNumbers(coupon.nearestAmount)} €
                    </>
                  ) : (
                    `${t('payment:percentageDiscount')} `
                  )}
                  {coupon.value > 0 && <b>-{coupon.value}%</b>}
                </Typography>
              )}
              {coupon.type === ShoppingCartCouponType.PercentageDiscountFromOriginalPrice && (
                <Typography variant="body2">
                  {coupon.value === 0 ? (
                    <>
                      {t('payment:couponMinimumPrice')}
                      {roundDoubleNumbers(coupon.nearestAmount)} €
                    </>
                  ) : (
                    `${t('payment:percentageDiscountFromOriginalPrice')} `
                  )}
                  {coupon.value > 0 && <b>-{coupon.value}%</b>}
                </Typography>
              )}
              {coupon.type === ShoppingCartCouponType.CreditsAfterPurchase && (
                <Typography variant="body2">
                  {t('payment:creditsCoupon')}
                  <b>{roundDoubleNumbers(coupon.value)} €</b>
                </Typography>
              )}
            </Grid>
            <Grid size="auto">
              <CheckoutButton
                variant="contained"
                disabled={inProgress}
                onClick={handleCancelCouponClick}
                loading={inProgress}
                loadingIndicator={<ButtonLoadingIndicator size={18} />}
              >
                {t('order:cancel')}
              </CheckoutButton>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
